import * as React from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import useDeepCompareEffect from 'use-deep-compare-effect'
import { useStyletron } from "baseui";
import { Skeleton } from "baseui/skeleton";
import { Notification, KIND } from 'baseui/notification';
import { ErrorBoundary } from "../ErrorBoundary";
import { mapTheme } from "./styles";
import { useEffect } from "react";

const apikey = "AIzaSyC7ZMJkUfxQ06j2Vu7RoguILSUJJJooQx8"


export type TMapLocation = {
  latitude: number,
  longitude: number,
}
export type TMarker = {
  latitude: number,
  longitude: number,
  title: string,
}

interface GoogleMapProps {
  markers: TMarker[],
  width?: string,
  height?: string,
  initialZoom?: number,
  centerPoint?: TMapLocation
}

const GoogleMapWrapper = ({ markers, initialZoom = 12, width = "300px", height = "300px", centerPoint }: GoogleMapProps) => {
  return (
    <ErrorBoundary errorComponent={
      <Skeleton animation
        rows={1}
        overrides={{
          Row: {
            style: {
              height: '300px',
              width: width
            },
          },
        }}
      />
    }>
      <GoogleMap markers={markers} initialZoom={initialZoom} width={width} height={height} centerPoint={centerPoint}></GoogleMap>
    </ErrorBoundary>)
}

export default React.memo(GoogleMapWrapper);


const GoogleMap = ({ markers, initialZoom = 12, width = "300px", height = "300px", centerPoint }: GoogleMapProps) => {
  const [css, theme] = useStyletron();
  const [zoom, setZoom] = React.useState(initialZoom); // initial zoom
  const [center, setCenter] = React.useState<google.maps.LatLngLiteral>();
  const [bounds, setBounds] = React.useState<google.maps.LatLngBounds>();

  const mapStyles = mapTheme(theme)

  useEffect(() => {
    if (centerPoint) {
      setCenter({
        lat: centerPoint?.latitude,
        lng: centerPoint?.longitude
      })
    }
    else if (markers[0] !== undefined) {
      setCenter({
        lat: markers[0]?.latitude,
        lng: markers[0]?.longitude
      })
    }
  }, [centerPoint, markers])

  useEffect(() => {
    if (markers && markers.length > 1) {
      var latlngbounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();
      for (var i = 0; i < markers.length; i++) {
        latlngbounds.extend({ lat: markers[i].latitude, lng: markers[i].longitude });

      }
      setBounds(latlngbounds);
    }
  }, [markers])

  try {
    // MAP styles: see https://developers.google.com/maps/documentation/maps-static/styling
    return (
      <div className={css({ height: height, width: width })}>
        {markers ?
          <Wrapper apiKey={apikey} render={(status) => <>{render(status, width, height)}</>} >
            <Map center={center} disableDefaultUI={true} bounds={bounds}
              styles={mapStyles}
              zoom={zoom} style={{ flexGrow: "1", height: "100%" }}>
              {markers?.map((marker: TMarker, index: number) =>
                <Marker key={marker.title} position={{ lat: marker.latitude, lng: marker.longitude }} title={marker.title} />
              )}
            </Map>
          </Wrapper>
          : <>{render(Status.LOADING, width, height)}</>
        }
      </div>
    )
  }
  catch
  {
    return <></>
  }
}

const render = (status: Status, width: any, height: any) => {

  if (status === Status.FAILURE)
    return (
      <Notification kind={KIND.negative}>
        There has been an error loading map
      </Notification>)
  return (
    <Skeleton animation
      rows={1}
      overrides={{
        Row: {
          style: {
            height: height,
            width: width
          },
        },
      }}
    />
  )
};


interface MapProps extends google.maps.MapOptions {
  style?: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  children?: React.ReactNode;
  bounds?: google.maps.LatLngBounds;
}

const Map: React.FC<MapProps> = ({
  onClick,
  onIdle,
  children,
  style,
  bounds,
  ...options
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffect(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {}));
    }
  }, [ref, map]);

  React.useEffect(() => {
    if (map) {
      ["click", "idle"].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener("click", onClick);
      }

      if (onIdle) {
        map.addListener("idle", () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  React.useEffect(() => {
    if (bounds)
      map?.fitBounds(bounds);
  }, [map, bounds])

  return (
    <div ref={ref} style={style} >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          // @ts-ignore
          return React.cloneElement(child, { map });
        }
      })}
    </div>
  )
};



const Marker: React.FC<google.maps.MarkerOptions> = (options) => {
  const [marker, setMarker] = React.useState<google.maps.Marker>();

  React.useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker());
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
  }, [marker]);

  React.useEffect(() => {
    if (marker) {
      marker.setOptions(options);
    }
  }, [marker, options]);

  return null;
};